import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import TableRenderer from './TableRenderer';
import PlanStepTracker from './PlanStepTracker';

/**
 * The "Atom Response" workspace tab -- every table any chat message has
 * produced this session, stacked newest-last (never replacing older ones,
 * matching chat's own append-only convention). Auto-scrolls to and briefly
 * highlights whichever result is currently targeted by `highlightedResultId`
 * -- set either by AtomApp's own centralizing effect (a brand-new table just
 * arrived) or by MessageRenderer's inline "View in workspace" link (the user
 * clicked an older table's pointer while scrolled elsewhere in chat).
 *
 * Design history: project_atom_response_workspace_tab memory. Memory-bloat
 * management (capping/collapsing very long stacks) deliberately deferred --
 * user's own words, "we can decide later."
 */
const AtomResponsePanel = ({
  results,
  highlightedResultId,
  onHighlightHandled,
  resultRefs,
  colors,
  onTableRowAction,
  onTableBulkAction,
  onTableGlobalAction,
  onTableLinkClick,
}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!highlightedResultId) return;
    const node = resultRefs.current[highlightedResultId];
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // Highlight is transient -- clear it after the scroll/flash has had time
    // to be seen, so re-clicking the same link later re-triggers it.
    const timer = setTimeout(() => {
      onHighlightHandled && onHighlightHandled();
    }, 2000);
    return () => clearTimeout(timer);
  }, [highlightedResultId, resultRefs, onHighlightHandled]);

  if (results.length === 0) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" sx={{ color: colors.textMuted }}>
          Tables and plan progress from chat will appear here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={{ flex: 1, overflow: 'auto', p: 2 }}>
      {results.map((result) => {
        const isHighlighted = result.id === highlightedResultId;
        return (
          <Box
            key={result.id}
            ref={(node) => { resultRefs.current[result.id] = node; }}
            sx={{
              mb: 3,
              p: isHighlighted ? 1 : 0,
              borderRadius: 1,
              border: isHighlighted ? '1px solid #22c55e' : '1px solid transparent',
              bgcolor: isHighlighted ? 'rgba(34, 197, 94, 0.06)' : 'transparent',
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
            }}
          >
            {result.planStatus ? (
              <PlanStepTracker planStatus={result.planStatus} colors={colors} />
            ) : (
              <TableRenderer
                table={result.table}
                colors={colors}
                onRowAction={onTableRowAction}
                onBulkAction={onTableBulkAction}
                onGlobalAction={onTableGlobalAction}
                onLinkClick={onTableLinkClick}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default AtomResponsePanel;
